// Which cell this browser dials (wiki/feature-home-region.md §2.1, §2.2).

import { PUBLIC_CELLS } from '$env/static/public';
import { apiOrigin, ON_ADMIN_HOST, type ApiOptions } from './client';

const HOME_COOKIE = 'vd_home';
const HOME_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
const PROBE_CAP_MS = 1500;

interface Cell {
  id: string;
  origin: string;
}

function knownCells(): Cell[] {
  return PUBLIC_CELLS.split(',').flatMap((entry) => {
    const separator = entry.indexOf('=');
    const id = entry.slice(0, Math.max(separator, 0)).trim();
    const origin = entry.slice(separator + 1).trim().replace(/\/$/, '');
    return id && origin ? [{ id, origin }] : [];
  });
}

export function knownCellIds(): string[] {
  return knownCells().map((cell) => cell.id);
}

/** The origin of a cell in `PUBLIC_CELLS`; nothing else can name a host this app dials. */
export function cellOrigin(id: string): string | undefined {
  return knownCells().find((cell) => cell.id === id)?.origin;
}

function readHomeCookie(): string | undefined {
  return document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${HOME_COOKIE}=`))
    ?.slice(HOME_COOKIE.length + 1);
}

// The server also sets this cookie, on the zone one label above the cell host. A write must carry
// the same Domain or it would sit beside that cookie rather than replace it. A page outside the
// zone, as in local dev, can only hold a host-only cookie.
export function writeHomeCookie(id: string, origin: string): void {
  const zone = new URL(origin).hostname.split('.').slice(1).join('.');
  const { hostname, protocol } = location;
  const inZone = zone !== '' && (hostname === zone || hostname.endsWith(`.${zone}`));
  document.cookie =
    `${HOME_COOKIE}=${id}; Path=/; Max-Age=${HOME_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax` +
    (inZone ? `; Domain=.${zone}` : '') +
    (protocol === 'https:' ? '; Secure' : '');
}

// The first sample carries DNS and TLS setup, so it only warms the connection.
async function score(origin: string, signal: AbortSignal): Promise<number> {
  const samples: number[] = [];
  for (let i = 0; i < 3; i += 1) {
    const startedAt = performance.now();
    const response = await fetch(`${origin}/health/ping`, { credentials: 'omit', cache: 'no-store', signal });
    if (!response.ok) return Infinity;
    samples.push(performance.now() - startedAt);
  }
  return Math.min(samples[1], samples[2]);
}

async function fastestCell(cells: Cell[]): Promise<Cell | undefined> {
  const controller = new AbortController();
  const cap = setTimeout(() => controller.abort(), PROBE_CAP_MS);
  const scores = await Promise.all(
    cells.map((cell) => score(cell.origin, controller.signal).catch(() => Infinity)),
  );
  clearTimeout(cap);
  const best = Math.min(...scores);
  return best === Infinity ? undefined : cells[scores.indexOf(best)];
}

/** The origin this page load should dial, or nothing when the build's default stands. */
export async function resolveCellOrigin(): Promise<string | undefined> {
  if (ON_ADMIN_HOST) return undefined;
  const cells = knownCells();
  if (cells.length < 2) return undefined;

  const pinned = readHomeCookie();
  const home = cells.find((cell) => cell.id === pinned);
  if (home) return home.origin;

  const fastest = await fastestCell(cells);
  if (fastest) writeHomeCookie(fastest.id, fastest.origin);
  return fastest?.origin;
}

let crossing = false;

/** The page is about to reload onto the account's new cell, on purpose and with nothing to warn about. */
export function markCellCrossing(): void {
  crossing = true;
}

export function cellCrossing(): boolean {
  return crossing;
}

let switchPending = false;

/** True once a re-probe chose another cell, which the next navigation loads in full. */
export function cellSwitchPending(): boolean {
  return switchPending;
}

/**
 * A guest's cell stopped answering. The switch waits for a navigation, because reloading here
 * would end a take in progress.
 */
export async function reprobeCell(): Promise<void> {
  if (ON_ADMIN_HOST) return;
  const cells = knownCells();
  if (cells.length < 2) return;
  const fastest = await fastestCell(cells);
  if (!fastest) return;
  writeHomeCookie(fastest.id, fastest.origin);
  switchPending = fastest.origin !== apiOrigin();
}

/**
 * Options that send one request to the cell a link names. These routes are anonymous and the
 * browser's session belongs to its own cell, whose token the named cell's gate would answer
 * with 421, so no credentials go along.
 */
export function cellLinkOptions(id: string | null | undefined): Pick<ApiOptions, 'origin' | 'credentials'> {
  const origin = id ? cellOrigin(id) : undefined;
  return origin && origin !== apiOrigin() ? { origin, credentials: 'omit' } : {};
}
